import type { WebFaqByLanguageGetResponse, WebFaqCollection } from "@/autogenerated/model";
import { pickBrowserLanguage } from "@/i18n/browser-language";
import { isLocale, type Locales } from "@/i18n/i18nConfig";

// Pure and edge-safe: the middleware imports this, so nothing here may reach next/headers.

const faqHost = "faq.reshopper.com";
export const faqOrigin = `https://${faqHost}`;

const marketplaceOrigin = "https://reshopper.com";

// Where the links on a FAQ page point. faq.reshopper.com serves the FAQ from its root and has no
// marketplace pages; every other host serves the FAQ under /faq beside the marketplace.
export type FaqLinkBase = { faqPath: string; marketplaceOrigin: string };

export function getFaqLinkBase(isOnFaqHost: boolean): FaqLinkBase {
	return isOnFaqHost ? { faqPath: "", marketplaceOrigin } : { faqPath: "/faq", marketplaceOrigin: "" };
}

export function isFaqHost(host: string | null | undefined): boolean {
	return host?.split(":")[0].toLowerCase() === faqHost;
}

// The shape the backend allows a slug to take; anything else in a URL cannot name a FAQ page.
export function isFaqSlug(value: string): boolean {
	return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value);
}

export function buildFaqPath(linkBase: FaqLinkBase, language: Locales, ...slugs: string[]): string {
	return `${linkBase.faqPath}/${[language, ...slugs].join("/")}`;
}

// The same page in every language, as a path from the FAQ's root: what hreflang, the canonical address
// and the language switch are built from.
export type FaqPathsByLanguage = Record<Locales, string>;

export function buildFaqPathsByLanguage(getSlugs: (language: Locales) => string[]): FaqPathsByLanguage {
	const buildPath = (language: Locales) => `/${[language, ...getSlugs(language)].join("/")}`;
	return { da: buildPath("da"), en: buildPath("en"), nob: buildPath("nob") };
}

export function withSearchParams(path: string, searchParams: Record<string, string | string[] | undefined>): string {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(searchParams)) {
		for (const item of [value ?? []].flat()) {
			query.append(key, item);
		}
	}

	const queryString = query.toString();
	return queryString ? `${path}?${queryString}` : path;
}

export function pickFaqLanguage(acceptLanguage: string | null): Locales {
	return pickBrowserLanguage(acceptLanguage) ?? "da";
}

export type FaqRoute = { action: "next" } | { action: "rewrite"; pathname: string } | { action: "redirect"; pathname: string; status: 301 | 307 };

// How the middleware answers a FAQ request, or undefined when the request is not for the FAQ.
export function routeFaqRequest(host: string | null, pathname: string, acceptLanguage: string | null): FaqRoute | undefined {
	const [firstSegment, ...otherSegments] = pathname.split("/").filter(Boolean);

	if (!isFaqHost(host)) {
		if (firstSegment !== "faq") {
			return undefined;
		}

		// As on faq.reshopper.com, a path without a language gets the visitor's, and lands on the FAQ's own 404
		// when nothing is there.
		const startsWithFaqLanguage = otherSegments.length > 0 && isLocale(otherSegments[0]);
		return startsWithFaqLanguage
			? { action: "next" }
			: { action: "redirect", pathname: `/faq/${[pickFaqLanguage(acceptLanguage), ...otherSegments].join("/")}`, status: 307 };
	}

	if (firstSegment === undefined) {
		return { action: "redirect", pathname: `/${pickFaqLanguage(acceptLanguage)}`, status: 307 };
	}

	if (firstSegment === "faq") {
		return { action: "redirect", pathname: `/${otherSegments.join("/")}`, status: 301 };
	}

	if (isLocale(firstSegment)) {
		return { action: "rewrite", pathname: `/faq${pathname}` };
	}

	// Help Scout's own addresses carried no language, and its site was Danish.
	const legacyHelpScoutPathKinds = ["article", "collection", "category"];
	const isLegacyHelpScoutPath = legacyHelpScoutPathKinds.includes(firstSegment) && otherSegments.length === 1;
	if (isLegacyHelpScoutPath) {
		return { action: "rewrite", pathname: `/faq/da/${firstSegment}/${otherSegments[0]}` };
	}

	return { action: "redirect", pathname: `/${pickFaqLanguage(acceptLanguage)}${pathname}`, status: 307 };
}

// The collection page's section for a category, which Help Scout's category addresses redirect to.
export function getFaqCategoryAnchor(categoryId: string): string {
	return `category-${categoryId}`;
}

// Help Scout addresses read `/article/{id}-{slug}`; only the id identifies the page.
export function parseLegacyHelpScoutId(segment: string): number | undefined {
	const match = /^(\d+)(?:-|$)/.exec(segment);
	return match ? Number(match[1]) : undefined;
}

// The Help Scout id an article was imported under. The import skipped duplicate articles, whose old addresses
// still circulate (installed apps link 239), so each one leads to the article it duplicates.
export function getImportedHelpScoutArticleId(helpScoutId: number): number {
	const originalIdByDuplicateId: Record<number, number> = { 239: 249, 242: 247, 243: 248, 244: 249, 245: 250 };
	return originalIdByDuplicateId[helpScoutId] ?? helpScoutId;
}

// A collection is addressed by its slug in the page's language, or by its Danish slug, which the page
// then redirects to the language's own.
export function findFaqCollectionBySlug(faq: WebFaqByLanguageGetResponse, language: Locales, slug: string): WebFaqCollection | undefined {
	return faq.collections.find((collection) => collection.slugs[language] === slug) ?? faq.collections.find((collection) => collection.slugs.da === slug);
}

// Article bodies link to each other by their Danish address on faq.reshopper.com (or by the /faq path
// a marketplace host serves it on). Each such link is pointed at the same article in the reader's
// language, on the host the reader is on.
export function localiseFaqHref(href: string, faq: WebFaqByLanguageGetResponse, language: Locales, linkBase: FaqLinkBase): string {
	const danishArticleAddress = parseDanishFaqArticleHref(href);
	if (!danishArticleAddress) {
		return isMarketplacePath(href) ? `${linkBase.marketplaceOrigin}${href}` : href;
	}

	const collection = faq.collections.find((candidate) => candidate.slugs.da === danishArticleAddress.collectionSlug);
	const article = faq.articles.find((candidate) => candidate.collectionId === collection?.id && candidate.slugs.da === danishArticleAddress.articleSlug);
	if (!collection || !article) {
		return href;
	}

	return `${buildFaqPath(linkBase, language, collection.slugs[language], article.slugs[language])}${danishArticleAddress.hash}`;
}

function parseDanishFaqArticleHref(href: string) {
	const isRelativePath = href.startsWith("/") && !href.startsWith("//");
	let url: URL;
	try {
		url = new URL(href, faqOrigin);
	} catch {
		return undefined;
	}

	const segments = url.pathname.split("/").filter(Boolean);
	const isOnFaqHost = url.host === faqHost && !isRelativePath;
	const isUnderFaqPath = segments[0] === "faq" && (isRelativePath || isMarketplaceHost(url.host) || url.host === faqHost);
	const pathSegments = isUnderFaqPath ? segments.slice(1) : isOnFaqHost ? segments : [];
	const [language, collectionSlug, articleSlug] = pathSegments;

	const isDanishArticlePath = pathSegments.length === 3 && language === "da";
	return isDanishArticlePath ? { collectionSlug, articleSlug, hash: url.hash } : undefined;
}

// A root-relative link in an article means the marketplace, which faq.reshopper.com does not serve.
function isMarketplacePath(href: string) {
	return href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/faq/");
}

function isMarketplaceHost(host: string) {
	return host === "reshopper.com" || host === "www.reshopper.com";
}
